import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Folder,
  BarChart3,
  Users,
  Settings,
  Plus,
  UserPlus,
  FileText,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  Menu,
  X,
} from 'lucide-react';
import { localizedDashboardContent } from '../data/dashboardContent';
import type { DashboardStatusTone } from '../data/dashboardContent';
import type { Locale } from '../i18n';
import { getAccessibleAccentColor, getAccessibleTextColor, withHexAlpha } from '../utils/color';
import type { Theme } from '../types/theme';

interface DashboardPageProps {
  theme: Theme;
  locale?: Locale;
  isMobile?: boolean;
}

type DashboardView = 'overview' | 'projects' | 'analytics' | 'members' | 'settings';

const dashboardViewOrder: DashboardView[] = ['overview', 'projects', 'analytics', 'members', 'settings'];

const dashboardViewCopy: Record<Locale, Record<DashboardView, { title: string; subtitle: string }>> = {
  zh: {
    overview: { title: '仪表盘', subtitle: '欢迎回来，这里是项目的最新状态。' },
    projects: { title: '项目', subtitle: '跟踪项目进度、负责人和交付状态。' },
    analytics: { title: '分析', subtitle: '查看收入、使用和转化趋势。' },
    members: { title: '成员', subtitle: '管理团队成员、角色和权限状态。' },
    settings: { title: '设置', subtitle: '配置工作区偏好、通知和安全选项。' },
  },
  en: {
    overview: { title: 'Dashboard', subtitle: "Welcome back. Here's what's happening with your projects." },
    projects: { title: 'Projects', subtitle: 'Track project progress, owners, and delivery status.' },
    analytics: { title: 'Analytics', subtitle: 'Review revenue, usage, and conversion trends.' },
    members: { title: 'Members', subtitle: 'Manage team members, roles, and permission states.' },
    settings: { title: 'Settings', subtitle: 'Configure workspace preferences, notifications, and security options.' },
  },
};

const analyticsRows: Record<Locale, Array<{ label: string; value: string; detail: string }>> = {
  zh: [
    { label: '获客', value: '12.8k', detail: '自然搜索贡献 42%' },
    { label: '激活', value: '68%', detail: '新用户完成首个项目' },
    { label: '留存', value: '41%', detail: '30 天团队留存' },
  ],
  en: [
    { label: 'Acquisition', value: '12.8k', detail: 'Organic search contributes 42%' },
    { label: 'Activation', value: '68%', detail: 'New users completed first project' },
    { label: 'Retention', value: '41%', detail: '30-day team retention' },
  ],
};

const members: Record<Locale, Array<{ name: string; role: string; team: string; status: string }>> = {
  zh: [
    { name: 'Alex Chen', role: '管理员', team: '产品', status: '在线' },
    { name: 'Maya Patel', role: '设计负责人', team: '设计', status: '在线' },
    { name: 'Noah Smith', role: '工程师', team: '平台', status: '忙碌' },
    { name: 'Iris Wang', role: '数据分析', team: '增长', status: '离线' },
  ],
  en: [
    { name: 'Alex Chen', role: 'Admin', team: 'Product', status: 'Online' },
    { name: 'Maya Patel', role: 'Design Lead', team: 'Design', status: 'Online' },
    { name: 'Noah Smith', role: 'Engineer', team: 'Platform', status: 'Busy' },
    { name: 'Iris Wang', role: 'Analyst', team: 'Growth', status: 'Offline' },
  ],
};

const settingsGroups: Record<Locale, Array<{ title: string; items: string[] }>> = {
  zh: [
    { title: '工作区', items: ['公开项目索引', '每周摘要邮件', '实验功能'] },
    { title: '安全', items: ['双重验证', 'SSO 登录', '访问日志'] },
  ],
  en: [
    { title: 'Workspace', items: ['Public project index', 'Weekly digest email', 'Experimental features'] },
    { title: 'Security', items: ['Two-factor authentication', 'SSO login', 'Access logs'] },
  ],
};

const iconMap: Record<string, LucideIcon> = {
  'layout-dashboard': LayoutDashboard,
  folder: Folder,
  'bar-chart': BarChart3,
  users: Users,
  settings: Settings,
  plus: Plus,
  'user-plus': UserPlus,
  'file-text': FileText,
};

export function DashboardPage({ theme, locale = 'en', isMobile = false }: DashboardPageProps) {
  const { colors, typography, shadow } = theme;
  const content = localizedDashboardContent[locale];
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeView, setActiveView] = useState<DashboardView>('overview');
  const [settingToggles, setSettingToggles] = useState<Record<string, boolean>>({});
  const activePage = dashboardViewCopy[locale][activeView];
  const primaryText = getAccessibleTextColor(colors.onPrimary, colors.primary);
  const pageTextPrimary = getAccessibleTextColor(colors.textPrimary, colors.background);
  const pageTextSecondary = getAccessibleTextColor(colors.textSecondary, colors.background);
  const surfaceTextPrimary = getAccessibleTextColor(colors.textPrimary, colors.surface);
  const surfaceTextSecondary = getAccessibleTextColor(colors.textSecondary, colors.surface);
  const surfaceTextMuted = getAccessibleTextColor(colors.textMuted, colors.surface, 3);
  const elevatedBackground = colors.surfaceElevated || colors.surface;
  const elevatedText = getAccessibleTextColor(colors.textPrimary, elevatedBackground);
  const surfaceAccent = getAccessibleAccentColor(colors.primary, colors.surface, colors.primaryHover);
  const successText = getAccessibleAccentColor(colors.success || colors.primary, colors.surface, colors.primaryHover, 3);
  const errorText = getAccessibleAccentColor(colors.error || colors.primaryHover, colors.surface, colors.primary, 3);
  const warningText = getAccessibleAccentColor(colors.warning || '#b45309', colors.surface, colors.primaryHover, 3);

  const handleViewChange = (view: DashboardView) => {
    setActiveView(view);
    setSidebarOpen(false);
  };

  const getQuickActionTarget = (icon: string): DashboardView => {
    const actionTargets: Record<string, DashboardView> = {
      plus: 'projects',
      'user-plus': 'members',
      'file-text': 'analytics',
      'bar-chart': 'analytics',
    };

    return actionTargets[icon] || 'overview';
  };

  const toggleSetting = (settingId: string, defaultValue: boolean) => {
    setSettingToggles((currentToggles) => ({
      ...currentToggles,
      [settingId]: !(currentToggles[settingId] ?? defaultValue),
    }));
  };

  const renderNav = () => (
    <nav className="flex-1 space-y-1 p-3" aria-label="Dashboard navigation">
      {content.sidebar.menuItems.map((item, index) => {
        const Icon = iconMap[item.icon] || LayoutDashboard;
        const viewId = dashboardViewOrder[index] || 'overview';
        const isActive = activeView === viewId;
        return (
          <button
            key={index}
            type="button"
            onClick={() => handleViewChange(viewId)}
            aria-current={isActive ? 'page' : undefined}
            className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors"
            style={{
              backgroundColor: isActive ? withHexAlpha(colors.primary, '1f') : 'transparent',
              color: isActive ? surfaceAccent : surfaceTextSecondary,
            }}
          >
            <Icon size={18} aria-hidden="true" />
            {item.label}
          </button>
        );
      })}
    </nav>
  );

  const renderUser = () => (
    <div className="flex items-center gap-3 border-t p-4" style={{ borderColor: colors.border }}>
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold"
        style={{ backgroundColor: elevatedBackground, color: elevatedText }}
      >
        {content.sidebar.user.avatar}
      </div>
      <div>
        <p className="text-sm font-medium" style={{ color: surfaceTextPrimary }}>
          {content.sidebar.user.name}
        </p>
        <p className="text-xs" style={{ color: surfaceTextMuted }}>
          {content.sidebar.user.role}
        </p>
      </div>
    </div>
  );

  const renderStatsGrid = () => (
    <div className="grid grid-cols-2 gap-3">
      {content.stats.map((stat, index) => {
        const TrendIcon = stat.trend === 'up' ? TrendingUp : TrendingDown;
        const trendColor = stat.trend === 'up' ? successText : errorText;

        return (
          <div
            key={index}
            className="rounded-xl border p-3"
            style={{ backgroundColor: colors.surface, borderColor: colors.border, boxShadow: shadow.card }}
          >
            <p className="mb-1 text-xs font-medium" style={{ color: surfaceTextMuted }}>
              {stat.label}
            </p>
            <p className="mb-2 text-xl font-bold" style={{ color: surfaceTextPrimary }}>
              {stat.value}
            </p>
            <div className="flex items-center gap-1">
              <TrendIcon size={12} style={{ color: trendColor }} aria-hidden="true" />
              <span className="text-xs font-medium" style={{ color: trendColor }}>
                {stat.change}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderChartCard = (showDetailAction = true) => (
    <div className="rounded-xl border p-4" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold" style={{ color: surfaceTextPrimary }}>
          {content.chart.title}
        </h2>
        {showDetailAction && (
          <button
            type="button"
            onClick={() => handleViewChange('analytics')}
            className="flex min-h-11 items-center gap-1 rounded-lg px-2 text-xs font-medium"
            style={{ color: surfaceAccent }}
          >
            {content.chart.viewDetails}
            <ArrowUpRight size={12} aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="flex h-32 items-end justify-between gap-1">
        {content.chart.data.map((item, index) => {
          const maxValue = Math.max(...content.chart.data.map((dataItem) => dataItem.value));
          const heightPercent = (item.value / maxValue) * 100;

          return (
            <div key={index} className="flex flex-1 flex-col items-center gap-1">
              <div
                className="w-full rounded-t-lg transition-all"
                style={{ height: `${heightPercent}%`, backgroundColor: surfaceAccent, minHeight: '8px' }}
              />
              <span className="text-xs" style={{ color: surfaceTextMuted }}>
                {item.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderQuickActions = () => (
    <div className="rounded-xl border p-4" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
      <h2 className="mb-3 text-sm font-semibold" style={{ color: surfaceTextPrimary }}>
        {content.quickActionsTitle}
      </h2>
      <div className="space-y-2">
        {content.quickActions.map((action, index) => {
          const Icon = iconMap[action.icon] || Plus;
          return (
            <button
              key={index}
              type="button"
              onClick={() => handleViewChange(getQuickActionTarget(action.icon))}
              className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors"
              style={{
                backgroundColor: elevatedBackground,
                color: elevatedText,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Icon size={14} aria-hidden="true" />
              {action.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderTransactionsTable = (title = content.table.title) => (
    <div className="overflow-hidden rounded-xl border p-4" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
      <h2 className="mb-3 text-sm font-semibold" style={{ color: surfaceTextPrimary }}>
        {title}
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-xs">
          <thead>
            <tr className="border-b" style={{ borderColor: colors.border }}>
              {content.table.headers.map((header, index) => (
                <th key={index} className="px-3 py-2 text-left font-medium" style={{ color: surfaceTextMuted }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {content.table.rows.map((row, index) => {
              const statusStyles: Record<DashboardStatusTone, { text: string; background: string }> = {
                success: {
                  text: successText,
                  background: withHexAlpha(colors.success || colors.primary, '1f'),
                },
                warning: {
                  text: warningText,
                  background: '#f59e0b1f',
                },
                active: {
                  text: surfaceAccent,
                  background: withHexAlpha(colors.primary, '1f'),
                },
              };
              const statusStyle = statusStyles[row.statusTone];

              return (
                <tr key={index} className="border-b last:border-0" style={{ borderColor: colors.border }}>
                  <td className="px-3 py-2 font-medium" style={{ color: surfaceTextPrimary }}>
                    {row.customer}
                  </td>
                  <td className="px-3 py-2" style={{ color: surfaceTextSecondary }}>
                    {row.project}
                  </td>
                  <td className="px-3 py-2 font-medium" style={{ color: surfaceTextPrimary }}>
                    {row.amount}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className="inline-block rounded-full px-2 py-1 text-xs font-medium"
                      style={{ backgroundColor: statusStyle.background, color: statusStyle.text }}
                    >
                      {row.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6">
      {renderStatsGrid()}
      {renderChartCard()}
      {renderQuickActions()}
      {renderTransactionsTable()}
    </div>
  );

  const renderProjects = () => (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-3">
        {content.table.rows.slice(0, 3).map((row, index) => (
          <div
            key={row.customer}
            className="rounded-xl border p-4"
            style={{ backgroundColor: colors.surface, borderColor: colors.border, boxShadow: shadow.card }}
          >
            <div className="mb-4 flex items-center justify-between gap-2">
              <Folder size={18} style={{ color: surfaceAccent }} aria-hidden="true" />
              <span className="rounded-full px-2 py-1 text-xs" style={{ backgroundColor: withHexAlpha(colors.primary, '1f'), color: surfaceAccent }}>
                {row.status}
              </span>
            </div>
            <h2 className="text-sm font-semibold" style={{ color: surfaceTextPrimary }}>
              {row.project}
            </h2>
            <p className="mt-1 text-xs" style={{ color: surfaceTextMuted }}>
              {row.customer}
            </p>
            <div className="mt-4 h-2 overflow-hidden rounded-full" style={{ backgroundColor: colors.border }}>
              <div
                className="h-full rounded-full"
                style={{ width: `${56 + index * 16}%`, backgroundColor: surfaceAccent }}
              />
            </div>
          </div>
        ))}
      </div>
      {renderTransactionsTable(locale === 'zh' ? '项目列表' : 'Project list')}
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      {renderStatsGrid()}
      {renderChartCard(false)}
      <div className="grid gap-3 md:grid-cols-3">
        {analyticsRows[locale].map((row) => (
          <div
            key={row.label}
            className="rounded-xl border p-4"
            style={{ backgroundColor: colors.surface, borderColor: colors.border }}
          >
            <p className="text-xs font-medium" style={{ color: surfaceTextMuted }}>
              {row.label}
            </p>
            <p className="mt-2 text-2xl font-bold" style={{ color: surfaceTextPrimary }}>
              {row.value}
            </p>
            <p className="mt-2 text-xs" style={{ color: surfaceTextSecondary }}>
              {row.detail}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMembers = () => (
    <div className="space-y-4">
      {members[locale].map((member, index) => (
        <div
          key={member.name}
          className="flex items-center justify-between gap-4 rounded-xl border p-4"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        >
          <div className="flex min-w-0 items-center gap-3">
            <div
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold"
              style={{ backgroundColor: index === 0 ? colors.primary : elevatedBackground, color: index === 0 ? primaryText : elevatedText }}
            >
              {member.name.split(' ').map((part) => part[0]).join('').slice(0, 2)}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold" style={{ color: surfaceTextPrimary }}>
                {member.name}
              </h2>
              <p className="truncate text-xs" style={{ color: surfaceTextMuted }}>
                {member.role} / {member.team}
              </p>
            </div>
          </div>
          <span className="rounded-full px-2 py-1 text-xs font-medium" style={{ backgroundColor: withHexAlpha(colors.primary, '1f'), color: surfaceAccent }}>
            {member.status}
          </span>
        </div>
      ))}
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-4">
      {settingsGroups[locale].map((group, groupIndex) => (
        <div
          key={group.title}
          className="rounded-xl border p-4"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        >
          <h2 className="mb-4 text-sm font-semibold" style={{ color: surfaceTextPrimary }}>
            {group.title}
          </h2>
          <div className="space-y-3">
            {group.items.map((item, itemIndex) => {
              const settingId = `${groupIndex}-${itemIndex}`;
              const defaultEnabled = (groupIndex + itemIndex) % 2 === 0;
              const enabled = settingToggles[settingId] ?? defaultEnabled;
              return (
                <div key={item} className="flex min-h-11 items-center justify-between gap-4 rounded-lg border px-3" style={{ borderColor: colors.border }}>
                  <span className="text-sm" style={{ color: surfaceTextSecondary }}>
                    {item}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleSetting(settingId, defaultEnabled)}
                    aria-label={item}
                    aria-pressed={enabled}
                    className="flex h-6 w-11 items-center rounded-full p-0.5 transition-colors"
                    style={{ backgroundColor: enabled ? colors.primary : colors.border }}
                  >
                    <span
                      className="block h-5 w-5 rounded-full transition-transform"
                      style={{ backgroundColor: primaryText, transform: enabled ? 'translateX(20px)' : 'translateX(0)' }}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="rounded-xl border p-4" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
        <h2 className="text-sm font-semibold" style={{ color: errorText }}>
          {locale === 'zh' ? '危险区' : 'Danger zone'}
        </h2>
        <p className="mt-2 text-xs" style={{ color: surfaceTextMuted }}>
          {locale === 'zh' ? '用于测试破坏性操作、警示色和边界状态。' : 'Used to test destructive actions, warning color, and edge states.'}
        </p>
        <button
          type="button"
          className="mt-4 min-h-11 rounded-lg border px-4 text-sm font-medium"
          style={{ borderColor: errorText, color: errorText, backgroundColor: 'transparent' }}
        >
          {locale === 'zh' ? '停用工作区' : 'Deactivate workspace'}
        </button>
      </div>
    </div>
  );

  const renderActiveView = () => {
    const renderers: Record<DashboardView, () => JSX.Element> = {
      overview: renderOverview,
      projects: renderProjects,
      analytics: renderAnalytics,
      members: renderMembers,
      settings: renderSettings,
    };

    return renderers[activeView]();
  };

  return (
    <div
      className="relative flex min-h-screen"
      style={{
        backgroundColor: colors.background,
        fontFamily: typography.bodyFont,
      }}
    >
      {isMobile && (
        <>
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open dashboard navigation"
            className="absolute left-4 top-4 z-40 flex h-11 w-11 items-center justify-center rounded-lg"
            style={{ backgroundColor: colors.primary, color: primaryText }}
          >
            <Menu size={20} aria-hidden="true" />
          </button>

          {sidebarOpen && (
            <div
              className="absolute inset-0 z-50"
              style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)' }}
              onClick={() => setSidebarOpen(false)}
              role="presentation"
            >
              <aside
                className="flex h-full w-72 max-w-[88vw] flex-col"
                style={{ backgroundColor: colors.surface, borderColor: colors.border }}
                onClick={(event) => event.stopPropagation()}
                aria-label="Dashboard navigation"
              >
                <div className="flex items-center justify-between border-b p-4" style={{ borderColor: colors.border }}>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex h-8 w-8 items-center justify-center rounded-lg font-bold"
                      style={{ backgroundColor: colors.primary, color: primaryText }}
                    >
                      N
                    </div>
                    <span className="font-semibold" style={{ color: surfaceTextPrimary }}>
                      {content.sidebar.logo}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    aria-label="Close dashboard navigation"
                    className="flex h-11 w-11 items-center justify-center rounded-lg"
                    style={{ color: surfaceTextSecondary }}
                  >
                    <X size={20} aria-hidden="true" />
                  </button>
                </div>

                {renderNav()}
                {renderUser()}
              </aside>
            </div>
          )}
        </>
      )}

      {!isMobile && (
        <aside
          className="flex w-64 flex-shrink-0 flex-col border-r"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
          aria-label="Dashboard navigation"
        >
          <div className="flex items-center gap-2 border-b p-4" style={{ borderColor: colors.border }}>
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg font-bold"
              style={{ backgroundColor: colors.primary, color: primaryText }}
            >
              N
            </div>
            <span className="text-lg font-semibold" style={{ color: surfaceTextPrimary, letterSpacing: '0' }}>
              {content.sidebar.logo}
            </span>
          </div>

          {renderNav()}
          {renderUser()}
        </aside>
      )}

      <main className="flex-1 overflow-auto">
        <div className="p-4">
          <div className={isMobile ? 'mb-6 pl-14' : 'mb-6'}>
            <h1
              className="mb-2 text-xl font-bold"
              style={{
                color: pageTextPrimary,
                fontFamily: typography.headingFont,
                letterSpacing: '0',
              }}
            >
              {activePage.title}
            </h1>
            <p className="text-sm" style={{ color: pageTextSecondary }}>
              {activePage.subtitle}
            </p>
          </div>

          {renderActiveView()}
        </div>
      </main>
    </div>
  );
}
